import { RootState } from "../index";
import {createDraftSafeSelector, createEntityAdapter, createSlice, EntityId} from '@reduxjs/toolkit';
import { createAsset, destroyAsset, fetchAsset, fetchAssets, updateAsset } from "../../actions/community/assetsActions";

export interface AssetInterface {
  url: string;

  id: number;
  app_id: number;
  category_id: number;
  kind_id?: number | null;

  title: string;
  description: string;
  search_text: string;

  order: number;
  file_url: string;
  thumbnail_url: string;

  options: Object;

  published_at: string;
  published: boolean;
  created_at: string;
  updated_at: string;
}

type SilentType = { [key: string]: { ids: number[], count: number } };

export interface initialInterface {
  error: string | null;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  count: number;

  silent: SilentType;

  editId: EntityId | null;
  editError: string | null;

  newParams: AssetInterface | undefined;
  newError: string | null;

  newGroupParams: AssetInterface | undefined;
  newGroupError: string | null;
}

const initialState: initialInterface = {
  error: null,
  status: 'idle',
  count: 0,

  silent: {},

  editId: null,
  editError: null,

  newParams: undefined,
  newError: null,

  newGroupParams: undefined,
  newGroupError: null,
};

const assetsAdapter = createEntityAdapter<AssetInterface>();

const slice = createSlice({
  name: 'assets',
  initialState: assetsAdapter.getInitialState(initialState),
  reducers: {
    setNewParams: (state, action) => {
      state.newParams = action.payload;
      state.newError  = initialState.newError;
    },
    setNewError: (state, action) => {
      state.newError = action.payload;
    },
    setNewGroupParams: (state, action) => {
      state.newGroupParams = action.payload;
      state.newGroupError  = initialState.newGroupError;
    },
    setNewGroupError: (state, action) => {
      state.newGroupError = action.payload;
    },
    setEditId: (state, action) => {
      state.editId    = action.payload;
      state.editError = initialState.editError;
    },
    setEditError: (state, action) => {
      state.editError = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      // #index
      .addCase(fetchAssets.pending, (state, _action) => {
        state.status = 'loading'
      })
      .addCase(fetchAssets.fulfilled, (state, action) => {
        if (action.payload.silentId) {
          state.silent[action.payload.silentId] = state.silent[action.payload.silentId] || { ids: [], count: 0 };

          state.silent[action.payload.silentId].ids = action.payload.ids;
          state.silent[action.payload.silentId].count = action.payload.count;
          state.entities = { ...state.entities, ...action.payload.assets };
        } else {
          state.ids = action.payload.ids;
          state.entities = action.payload.assets;
          state.count = action.payload.count;
        }

        state.status = 'succeeded';
      })
      .addCase(fetchAssets.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || 'Unknown Error';
      })

      // #show
      .addCase(fetchAsset.fulfilled, (state, action) => {
        const asset = Object.values(action.payload.asset)[0];
        state.entities[asset.id] = asset;
      })

      // #create
      .addCase(createAsset.fulfilled, (state, action) => {
        const asset = Object.values(action.payload.asset)[0];
        state.entities[asset.id] = asset;
        state.ids.unshift(asset.id);
      })
      .addCase(createAsset.rejected, (state, action) => {
        const error = Array.isArray(action.payload) ? action.payload[0] : null;
        state.newError = error || action.error.message || 'Unknown Error';
      })

      // #update
      .addCase(updateAsset.fulfilled, (state, action) => {
        const asset = Object.values(action.payload.asset)[0];
        state.entities[asset.id] = asset;
      })
      .addCase(updateAsset.rejected, (state, action) => {
        const error = Array.isArray(action.payload) ? action.payload[0] : null;
        state.editError = error || action.error.message || 'Unknown Error';
      })

      // #destroy
      .addCase(destroyAsset.fulfilled, (state, action) => {
        assetsAdapter.removeOne(state, action.payload.id);
      })
  }
});

const reducer = slice.reducer;
export default reducer;

export const {
  setNewParams,
  setNewError,
  setNewGroupParams,
  setNewGroupError,
  setEditId,
  setEditError
} = slice.actions;

export const selectSilent = (state: RootState) => state.community.assets.silent;
export const selectSilentIds = createDraftSafeSelector(
  [selectSilent, (state, silent) => silent],
  (silent, silentId) => silent ? silent[silentId]?.ids : []
);
export const selectSilentCount = createDraftSafeSelector(
  [selectSilent, (_state, silent) => silent],
  (silent, silentId) => silent ? silent[silentId]?.count : []
);

export const {
  selectById: selectAssetById,
  selectIds: selectAssetIds,
  selectEntities: selectAssetEntities
} = assetsAdapter.getSelectors<RootState>(state => state.community.assets);
